/**
 * SubscriptionTransactionItem
 * Flutter source: wallet_screen.dart - _subscriptionItem()
 *
 * Displays subscription and subscription renewal transactions.
 */
import React from 'react';
import type { AppTransactionEntity } from '@/domain/entities/transaction/appTransactionEntity';
import { AppTransactionType } from '@/domain/enums/transaction/AppTransactionType';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { useLocale } from '@/presentation/contexts/LocaleContext';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { useDateFormatters } from '@/presentation/utils/useDateFormatters';
import { MathUtils } from '@/domain/utils/mathUtils';
import { AdminUserAvatar } from './AdminUserAvatar';
import { WalletUserAvatar } from './WalletUserAvatar';

interface SubscriptionTransactionItemProps {
  transaction: AppTransactionEntity;
  currentUserId: string;
  isFromAdmin?: boolean;
}

export const SubscriptionTransactionItem: React.FC<SubscriptionTransactionItemProps> = ({
  transaction,
  currentUserId,
  isFromAdmin = false,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const { l10n } = useLocale();
  const dateFormatters = useDateFormatters();

  const isCurrentUser = transaction.usersInfo.userId === currentUserId;
  const isRenew = transaction.transactionType === AppTransactionType.subscriptionRenewed;
  const peerName = transaction.usersInfo.peerName ?? '';

  let title: string;
  if (isCurrentUser) {
    if (isRenew) {
      title = `${l10n.wallet.your} ${l10n.wallet.subscriptionRenewed} ${l10n.wallet.to} ${peerName}`;
    } else {
      title = `${l10n.wallet.you} ${l10n.wallet.subscribed} ${peerName}`;
    }
  } else {
    const name = transaction.usersInfo.name;
    if (isRenew) {
      // Admin mode appends the localized "to {peerName}" connector. The web baseline
      // consolidated Flutter's split "subscribtion … has been renewed" into the single
      // `subscriptionRenewed` key, so the connector is appended at the END of the phrase
      // (accepted UI-string divergence from Flutter's mid-phrase insertion; see task plan).
      const to = isFromAdmin ? `${l10n.wallet.to} ${transaction.usersInfo.peerName ?? ''}` : '';
      title = `${name}'s ${l10n.wallet.subscriptionRenewed} ${to}`.trimEnd();
    } else {
      // `l10n.wallet.subscribed` already ends in "to" — do NOT prepend `l10n.wallet.to`
      // here, or admin mode would emit a doubled "to". Append only the peer name.
      const subTo = isFromAdmin ? ` ${transaction.usersInfo.peerName ?? ''}` : '';
      title = `${name} ${l10n.wallet.subscribed}${subTo}`;
    }
  }

  const avatarUserId = isCurrentUser
    ? (transaction.usersInfo.peerUserId ?? '')
    : transaction.usersInfo.userId;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.sm }}>
      <WalletUserAvatar userId={avatarUserId} alt={transaction.usersInfo.name} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ color: theme.text.primary, fontSize: responsive.font.sm, fontWeight: responsive.fontWeight.medium }}>
          {title}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: responsive.gap.xs, marginTop: responsive.gap.xs }}>
          {transaction.subscriptionInfo && (
            <span style={{ color: theme.text.secondary, fontSize: responsive.font.xs }}>
              {MathUtils.removeTrailingZero(transaction.subscriptionInfo.coins)}
            </span>
          )}
          <AssetIcon
            path="/assets/images/coins.png"
            width={responsive.icon.small}
            height={responsive.icon.small}
            color={theme.exclusive}
          />
          <span style={{ color: theme.text.secondary, fontSize: responsive.font.xs }}>
            · {dateFormatters.formatDate(transaction.createdDate, true)}
          </span>
        </div>
      </div>

      {isFromAdmin && <AdminUserAvatar userId={transaction.usersInfo.peerUserId ?? ''} />}
    </div>
  );
};
